// Add this at the top of your App.js, right after the imports
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

// // Add this after ALL imports
const BASE_URL = process.env.PUBLIC_URL || '';

// ── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg: "#07090f", surface: "#0d1220", panel: "#111827", border: "#1a2540",
  accent:"#00d4ff", accentDim:"#0099bb",
  green:"#00e5a0", amber:"#f5a623", red:"#ff4d6a", purple:"#a78bfa",
  textPrimary:"#dde8f8", textSecondary:"#6b88b0", textMuted:"#334466",
  mono:"'IBM Plex Mono','Courier New',monospace",
  sans:"'IBM Plex Sans','Segoe UI',system-ui,sans-serif",
};

// ── API Sources ───────────────────────────────────────────────────────────────
const SOURCES = {
  global:     { label:"COVID Global Stats",        url:"https://disease.sh/v3/covid-19/all",                                              tag:"disease.sh"   },
  historical: { label:"COVID Historical (all)",    url:"https://disease.sh/v3/covid-19/historical/all?lastdays=all",                      tag:"disease.sh"   },
  countries:  { label:"COVID by Country",          url:"https://disease.sh/v3/covid-19/countries?sort=cases",                            tag:"disease.sh"   },
  continents: { label:"COVID by Continent",        url:"https://disease.sh/v3/covid-19/continents",                                       tag:"disease.sh"   },
  fda:        { label:"FDA Adverse Events (24mo)", url:"https://api.fda.gov/drug/event.json?count=receivedate&limit=24",                  tag:"OpenFDA"      },
  wastewater: { label:"CDC Wastewater (NWSS)",     url:"https://data.cdc.gov/resource/2ew6-ywp6.json?$limit=500&$order=date_end%20DESC", tag:"data.cdc.gov" },
};

// ── Export / Import ───────────────────────────────────────────────────────────
const dlBlob = (content, name, type) => {
  const a = Object.assign(document.createElement("a"), {
    href: URL.createObjectURL(new Blob([content], { type })), download: name,
  });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};
const esc      = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
const toCSV    = rows => {
  if (!rows?.length) return "";
  const ks = Object.keys(rows[0]);
  return [ks.map(esc).join(","), ...rows.map(r => ks.map(k => esc(r[k])).join(","))].join("\n");
};
const exportCSV  = (rows, name) => rows?.length && dlBlob(toCSV(rows), `${name}.csv`, "text/csv");
const exportJSON = (rows, name) => rows?.length && dlBlob(JSON.stringify(rows, null, 2), `${name}.json`, "application/json");
const exportLogs = (logs, fmt) => {
  if (!logs?.length) return;
  if (fmt === "csv") exportCSV(logs.map(l => ({ time:l.t, level:l.level, service:l.svc, message:l.msg })), "pulse-logs");
  else dlBlob(logs.map(l => `[${l.t}] ${l.level.padEnd(5)} [${l.svc.padEnd(14)}] ${l.msg}`).join("\n"), "pulse-logs.txt", "text/plain");
};
const parseCSVText = text => {
  const lines = text.trim().split("\n").filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? ""]));
  });
};

// ── Data Transforms ───────────────────────────────────────────────────────────
const transformFDA = raw => (raw.results ?? []).map(r => ({
  date:     r.time ? `${String(r.time).slice(4,6)}/${String(r.time).slice(6,8)}` : "?",
  fullDate: String(r.time ?? ""),
  events:   r.count ?? 0,
}));

const transformWastewater = raw => {
  const byState = {};
  (raw ?? []).forEach(r => {
    const state = r.wwtp_jurisdiction ?? r.state_name ?? "Unknown";
    if (!byState[state]) byState[state] = { state, sites:0, detections:0, total:0 };
    byState[state].sites++;
    const p = parseFloat(r.detect_prop_15d ?? 0);
    byState[state].detections += isNaN(p) ? 0 : p;
    byState[state].total++;
  });
  return Object.values(byState)
    .map(s => ({ ...s, detectPct: s.total > 0 ? +(s.detections / s.total * 100).toFixed(1) : 0 }))
    .sort((a, b) => b.detectPct - a.detectPct);
};

// ── COVID historical range slicing ────────────────────────────────────────────
const RANGE_OPTIONS = [
  { label:"30 Days",  days:30   },
  { label:"90 Days",  days:90   },
  { label:"6 Months", days:180  },
  { label:"1 Year",   days:365  },
  { label:"2 Years",  days:730  },
  { label:"All Time", days:null },
];

function sliceHistorical(hist, days) {
  if (!hist?.cases) return [];
  const allEntries = Object.entries(hist.cases);
  const subset = days ? allEntries.slice(-days) : allEntries;
  const step = Math.max(1, Math.floor(subset.length / 60));
  return subset.filter((_, i) => i % step === 0).map(([date, cases]) => ({
    date,
    cases,
    deaths: hist.deaths?.[date] ?? 0,
  }));
}

// ── ETL Code Snippets ─────────────────────────────────────────────────────────
const PIPELINE_CODE = {
  global: `# disease.sh Global Stats — Pydantic model + fetch
import httpx, asyncio
from pydantic import BaseModel
from datetime import datetime

class CovidGlobal(BaseModel):
    cases:              int
    deaths:             int
    recovered:          int
    active:             int
    critical:           int
    casesPerOneMillion: float
    updated:            datetime

async def fetch_global() -> CovidGlobal:
    async with httpx.AsyncClient(timeout=10) as c:
        r = await c.get("https://disease.sh/v3/covid-19/all")
        r.raise_for_status()
    data = r.json()
    data["updated"] = datetime.fromtimestamp(data["updated"] / 1000)
    return CovidGlobal(**data)`,

  historical: `# COVID Historical ETL → Timeseries table (Airflow DAG)
import httpx, pandas as pd
from airflow.decorators import dag, task
import pendulum

@dag(schedule="@daily", start_date=pendulum.datetime(2024,1,1), catchup=False)
def covid_historical_etl():

    @task()
    def extract() -> dict:
        r = httpx.get(
            "https://disease.sh/v3/covid-19/historical/all?lastdays=all",
            timeout=30)
        r.raise_for_status()
        return r.json()

    @task()
    def transform(raw: dict) -> list[dict]:
        df = pd.DataFrame({
            "date":   pd.to_datetime(list(raw["cases"].keys())),
            "cases":  list(raw["cases"].values()),
            "deaths": list(raw["deaths"].values()),
        })
        df["delta_cases"]  = df["cases"].diff().fillna(0).astype(int)
        df["delta_deaths"] = df["deaths"].diff().fillna(0).astype(int)
        df["etl_ts"]       = pendulum.now("UTC")
        return df.to_dict(orient="records")

    @task()
    def load(records: list[dict]):
        import psycopg2, io
        conn = psycopg2.connect(dsn="postgresql://ph_user@pg-main/surveillance")
        buf  = io.StringIO()
        pd.DataFrame(records).to_csv(buf, index=False, header=False)
        buf.seek(0)
        with conn.cursor() as cur:
            cur.copy_expert(
                "COPY covid.historical(date,cases,deaths,"
                "delta_cases,delta_deaths,etl_ts) FROM STDIN CSV", buf)
        conn.commit(); conn.close()

    load(transform(extract()))

covid_historical_etl()`,

  fda: `# OpenFDA Adverse Events → Kafka → Postgres
from confluent_kafka import Producer
import httpx, json, asyncio
from app.models import FDAEvent
from datetime import datetime

PRODUCER_CFG = {"bootstrap.servers": "kafka-0:9092"}

async def stream_fda_events():
    producer = Producer(PRODUCER_CFG)
    async with httpx.AsyncClient() as c:
        r = await c.get(
            "https://api.fda.gov/drug/event.json"
            "?count=receivedate&limit=24",
            headers={"User-Agent":"PULSE/1.0 public-health-platform"})
        r.raise_for_status()
    for item in r.json().get("results", []):
        event = FDAEvent(
            receive_date = item["time"],
            event_count  = item["count"],
            ingested_at  = datetime.utcnow().isoformat(),
        )
        producer.produce(
            "fda.adverse_events",
            key   = str(event.receive_date),
            value = json.dumps(event.dict()),
        )
    producer.flush()
    print(f"Streamed FDA records → Kafka")

asyncio.run(stream_fda_events())`,

  wastewater: `# CDC NWSS Wastewater → PostGIS ETL
import httpx, pandas as pd
from sqlalchemy import create_engine
from pydantic import BaseModel, validator

class WastewaterSite(BaseModel):
    wwtp_jurisdiction: str
    detect_prop_15d:   float | None
    date_end:          str

    @validator("detect_prop_15d", pre=True)
    def coerce_float(cls, v):
        try: return float(v)
        except: return None

def etl_nwss():
    r = httpx.get(
        "https://data.cdc.gov/resource/2ew6-ywp6.json"
        "?$limit=500&$order=date_end DESC",
        timeout=30)
    r.raise_for_status()
    sites  = [WastewaterSite(**row) for row in r.json()
              if row.get("wwtp_jurisdiction")]
    df     = pd.DataFrame([s.dict() for s in sites])
    engine = create_engine(
        "postgresql+psycopg2://ph_user@pg-main/surveillance")
    df.to_sql("nwss_wastewater", engine, schema="cdc",
              if_exists="replace", index=False)
    print(f"Loaded {len(df)} sites → PostGIS")

etl_nwss()`,
};

// ── Utils ─────────────────────────────────────────────────────────────────────
const fmt = n =>
  n == null ? "—" :
  n >= 1e9 ? (n/1e9).toFixed(2)+"B" :
  n >= 1e6 ? (n/1e6).toFixed(2)+"M" :
  n >= 1e3 ? (n/1e3).toFixed(1)+"K" : String(n);
const fmtFull = n => n == null ? "—" : Number(n).toLocaleString();
const nowTs   = () => new Date().toTimeString().slice(0, 8);

// ── Micro UI ──────────────────────────────────────────────────────────────────
function Dot({ s, size = 8 }) {
  const c = { healthy:T.green, running:T.accent, success:T.green, warning:T.amber, error:T.red, idle:T.textMuted }[s] ?? T.textMuted;
  return <span style={{
    display:"inline-block", width:size, height:size, borderRadius:"50%",
    background:c, boxShadow:`0 0 ${size}px ${c}88`, flexShrink:0,
    animation:(s==="running"||s==="fetching")?"pulse 1.4s ease-in-out infinite":"none",
  }} />;
}
function Tag({ c = T.accent, children, onClick }) {
  return <span onClick={onClick} style={{
    fontSize:10, fontFamily:T.mono, color:c, border:`1px solid ${c}44`,
    background:`${c}12`, padding:"2px 8px", borderRadius:3, letterSpacing:.8,
    cursor:onClick?"pointer":"default", whiteSpace:"nowrap",
  }}>{children}</span>;
}
function MiniBar({ v, max = 100, c = T.accent }) {
  return (
    <div style={{ height:3, background:T.border, borderRadius:2, overflow:"hidden" }}>
      <div style={{ height:"100%", width:`${Math.min((v/max)*100,100)}%`, background:c,
        borderRadius:2, transition:"width .6s ease", boxShadow:`0 0 5px ${c}88` }} />
    </div>
  );
}
function Btn({ children, c = T.accent, onClick, disabled, sm }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background:`${c}18`, border:`1px solid ${c}55`, color:c,
      fontFamily:T.mono, fontSize:sm?9:11, letterSpacing:.8,
      padding:sm?"4px 9px":"7px 14px", borderRadius:4,
      cursor:disabled?"not-allowed":"pointer", opacity:disabled?.5:1,
      transition:"all .15s", whiteSpace:"nowrap",
    }}>{children}</button>
  );
}
function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      background:T.panel, border:`1px solid ${T.border}`, borderRadius:4,
      color:T.accent, fontFamily:T.mono, fontSize:10, letterSpacing:.8,
      padding:"4px 10px", cursor:"pointer", outline:"none",
    }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
function Spin() {
  return <span style={{
    display:"inline-block", width:10, height:10,
    border:`2px solid ${T.textMuted}`, borderTopColor:T.accent,
    borderRadius:"50%", animation:"spin .7s linear infinite",
  }} />;
}
function Spinner() {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, padding:40 }}>
      <div style={{ width:28, height:28, border:`2px solid ${T.border}`, borderTop:`2px solid ${T.accent}`,
        borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      <span style={{ fontFamily:T.mono, fontSize:11, color:T.textSecondary }}>FETCHING LIVE DATA…</span>
    </div>
  );
}
// cursor={false} on every BarChart Tooltip prevents the gray hover rectangle
const TTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6,
      padding:"10px 14px", fontFamily:T.mono, fontSize:11 }}>
      <p style={{ color:T.textSecondary, margin:"0 0 5px" }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color:p.color, margin:"2px 0" }}>
          {p.name}: <b>{fmtFull(p.value)}</b>
        </p>
      ))}
    </div>
  );
};

// ── Influenza Heatmap — compact, side-by-side with continent chart ────────────
function FluHeatmap() {
  const COLS = 10, ROWS = 5, TOTAL = COLS * ROWS;
  const [cells, setCells] = useState(() =>
    Array.from({ length: TOTAL }, () => Math.floor(Math.random() * 6))
  );
  useEffect(() => {
    const id = setInterval(() => {
      setCells(prev => prev.map(v => {
        const r = Math.random();
        if (r < 0.10) return Math.min(5, v + 1);
        if (r < 0.20) return Math.max(0, v - 1);
        return v;
      }));
    }, 1600);
    return () => clearInterval(id);
  }, []);
  const intensities = [0.07, 0.20, 0.38, 0.58, 0.78, 1.0];
  const labels      = ["Minimal","Low","Moderate","High","Very High","Widespread"];

  return (
    <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6,
      padding:"14px 16px", height:"100%", display:"flex", flexDirection:"column" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <p style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, margin:0, letterSpacing:1 }}>
          INFLUENZA ACTIVITY — LIVE REGIONAL HEATMAP
        </p>
        <Tag c={T.amber}>● UPDATING</Tag>
      </div>
      {/* Grid fills available width; row height fixed at 20px */}
      <div style={{ flex:1, display:"grid", gridTemplateColumns:`repeat(${COLS},1fr)`,
        gridTemplateRows:`repeat(${ROWS},1fr)`, gap:3, marginBottom:8 }}>
        {cells.map((v, i) => (
          <div key={i} style={{
            borderRadius:3,
            background:`rgba(245,166,35,${intensities[v]})`,
            transition:"background 1s ease",
            boxShadow:v>=4?`0 0 5px rgba(245,166,35,${intensities[v]*0.5})`:"none",
          }} />
        ))}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontFamily:T.mono, fontSize:9, color:T.textMuted, letterSpacing:.8 }}>LEVEL:</span>
        {labels.map((l, i) => (
          <span key={l} style={{ display:"flex", alignItems:"center", gap:3,
            fontFamily:T.sans, fontSize:9, color:T.textSecondary }}>
            <span style={{ width:9, height:9, borderRadius:2, flexShrink:0, display:"inline-block",
              background:`rgba(245,166,35,${intensities[i]})` }} />
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────
const TABS = [
  { id:"dashboard",  icon:"◈", label:"Dashboard"  },
  { id:"pipelines",  icon:"⟳", label:"Pipelines"  },
  { id:"covid",      icon:"⬡", label:"COVID-19"    },
  { id:"fda",        icon:"⊕", label:"FDA Events"  },
  { id:"wastewater", icon:"≈", label:"Wastewater"  },
  { id:"datasets",   icon:"⊞", label:"Datasets"    },
  { id:"logs",       icon:"≡", label:"Logs"        },
];
function Nav({ tab, setTab, countdown, onRefresh, busy, pipeStatus }) {
  return (
    <nav style={{ display:"flex", alignItems:"center", borderBottom:`1px solid ${T.border}`,
      background:T.surface, flexShrink:0, overflowX:"auto" }}>
      {TABS.map(t => (
        <button key={t.id} onClick={() => setTab(t.id)} style={{
          background:"none", border:"none", cursor:"pointer", padding:"12px 15px",
          fontFamily:T.mono, fontSize:10, letterSpacing:1, whiteSpace:"nowrap",
          color:tab===t.id?T.accent:T.textSecondary,
          borderBottom:tab===t.id?`2px solid ${T.accent}`:"2px solid transparent",
          transition:"all .2s", display:"flex", alignItems:"center", gap:6,
        }}>
          <span style={{ fontSize:12 }}>{t.icon}</span>
          {t.label.toUpperCase()}
          {pipeStatus?.[t.id] && <span style={{ marginLeft:1 }}><Dot s={pipeStatus[t.id]} size={6} /></span>}
        </button>
      ))}
      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:10, padding:"0 20px" }}>
        <span style={{ fontFamily:T.mono, fontSize:10, color:T.textMuted }}>
          refresh <span style={{ color:T.accent }}>{countdown}s</span>
        </span>
        <Btn onClick={onRefresh} disabled={busy} sm>{busy?"FETCHING…":"↺ REFRESH ALL"}</Btn>
      </div>
    </nav>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ data, loading }) {
  const [rangeIdx, setRangeIdx] = useState(1); // default 90d

  const g       = data.global;
  const hist    = data.historical;
  const conts   = data.continents;
  const ww      = useMemo(() => data.wastewater ? transformWastewater(data.wastewater) : null, [data.wastewater]);
  const fdaData = useMemo(() => data.fda ? transformFDA(data.fda) : null, [data.fda]);

  const selectedRange = RANGE_OPTIONS[rangeIdx];
  const chartData = useMemo(() => sliceHistorical(hist, selectedRange.days), [hist, selectedRange]);

  const contData = useMemo(() => (conts ?? []).map(c => ({
    name:(c.continent??"").replace("South America","S. Am").replace("North America","N. Am").replace("Australia-Oceania","Oceania"),
    cases:c.cases,
  })).filter(c => c.name && c.cases), [conts]);

  const wwDetect = ww ? (ww.reduce((s,r)=>s+r.detectPct,0)/(ww.length||1)).toFixed(1) : null;
  const fdaTotal = fdaData ? fdaData.reduce((s,d)=>s+d.events,0) : null;

  const kpis = [
    { label:"Total Cases",    val:g?.cases,       sub:`+${fmtFull(g?.todayCases??0)} today`,                  c:T.red    },
    { label:"Total Deaths",   val:g?.deaths,      sub:`+${fmtFull(g?.todayDeaths??0)} today`,                 c:T.amber  },
    { label:"Recovered",      val:g?.recovered,   sub:`${((g?.recovered/g?.cases*100)||0).toFixed(1)}% rate`, c:T.green  },
    { label:"Active Cases",   val:g?.active,      sub:`${fmtFull(g?.critical??0)} critical`,                  c:T.accent },
    { label:"FDA Events",     val:fdaTotal,       sub:"adverse drug events (24mo)",                           c:T.purple },
    { label:"WW Detect Rate", val:wwDetect!=null?wwDetect+"%":null, sub:"CDC NWSS avg",                       c:T.green  },
  ];

  // Show year label only when year changes (for "All Time" view)
  const xFormatter = (() => {
    let last = "";
    return (val) => {
      if (!val) return "";
      // date string is like "1/22/20" (M/D/YY)
      const yr = val.split("/").pop() ?? "";
      if (yr !== last) { last = yr; return `'${yr}`; }
      return "";
    };
  })();

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:20 }}>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6,
            padding:"14px 16px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:2,
              background:`linear-gradient(90deg,${k.c},transparent)` }} />
            <p style={{ fontFamily:T.mono, fontSize:8, color:T.textSecondary, letterSpacing:1.5, margin:"0 0 6px" }}>
              {k.label.toUpperCase()}
            </p>
            {(loading.global||(k.label==="FDA Events"&&loading.fda)||(k.label==="WW Detect Rate"&&loading.wastewater))
              ? <div style={{ height:28, display:"flex", alignItems:"center" }}><Spin /></div>
              : <p style={{ fontFamily:T.mono, fontSize:22, fontWeight:700, color:k.c, margin:"0 0 4px", lineHeight:1 }}>
                  {fmt(k.val)}
                </p>
            }
            <p style={{ fontFamily:T.sans, fontSize:10, color:T.textMuted, margin:0 }}>{k.sub}</p>
          </div>
        ))}
      </div>

      {/* COVID trend — full width, with range dropdown */}
      <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6, padding:18 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <p style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, margin:0, letterSpacing:1 }}>
            GLOBAL COVID-19 CUMULATIVE TREND
          </p>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <Tag c={T.accent}>disease.sh · LIVE</Tag>
            <Select
              value={rangeIdx}
              onChange={v => setRangeIdx(Number(v))}
              options={RANGE_OPTIONS.map((r,i) => ({ label:r.label, value:i }))}
            />
          </div>
        </div>
        {loading.historical && !hist
          ? <div style={{ height:200, display:"flex", alignItems:"center", justifyContent:"center" }}><Spin /></div>
          : <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top:4, right:4, left:0, bottom:0 }}>
                <defs>
                  <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={T.red}   stopOpacity={.3} />
                    <stop offset="95%" stopColor={T.red}   stopOpacity={0}  />
                  </linearGradient>
                  <linearGradient id="gD" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={T.amber} stopOpacity={.25} />
                    <stop offset="95%" stopColor={T.amber} stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
                <XAxis dataKey="date"
                  tick={{ fill:T.textMuted, fontSize:9, fontFamily:T.mono }}
                  tickLine={false}
                  tickFormatter={xFormatter}
                  interval="preserveStartEnd" />
                <YAxis tick={{ fill:T.textMuted, fontSize:9, fontFamily:T.mono }}
                  tickLine={false} tickFormatter={v => fmt(v)} width={48} />
                {/* Use stroke cursor instead of filled rectangle */}
                <Tooltip cursor={{ stroke:T.border, strokeWidth:1 }} content={<TTip />} />
                <Legend formatter={v => <span style={{ fontFamily:T.mono, fontSize:9, color:T.textSecondary }}>{v.toUpperCase()}</span>} />
                <Area type="monotone" dataKey="cases"  stroke={T.red}   fill="url(#gC)" strokeWidth={2} name="Cases"  dot={false} />
                <Area type="monotone" dataKey="deaths" stroke={T.amber} fill="url(#gD)" strokeWidth={2} name="Deaths" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
        }
      </div>

      {/* Row: continent bar + flu heatmap — equal height */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, minHeight:220 }}>
        {/* Continent chart — cursor={false} removes gray hover bar */}
        <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6, padding:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <p style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, margin:0, letterSpacing:1 }}>CASES BY CONTINENT</p>
            <Tag c={T.green}>LIVE</Tag>
          </div>
          {loading.continents && !conts
            ? <div style={{ height:160, display:"flex", alignItems:"center", justifyContent:"center" }}><Spin /></div>
            : <ResponsiveContainer width="100%" height={160}>
                <BarChart data={contData} layout="vertical" margin={{ left:0, right:8, top:0, bottom:0 }}>
                  <XAxis type="number" tick={{ fill:T.textMuted, fontSize:8, fontFamily:T.mono }}
                    tickLine={false} axisLine={false} tickFormatter={v => fmt(v)} />
                  <YAxis type="category" dataKey="name"
                    tick={{ fill:T.textSecondary, fontSize:9, fontFamily:T.mono }}
                    tickLine={false} axisLine={false} width={82} />
                  {/* cursor={false} — THIS removes the gray hover rectangle */}
                  <Tooltip cursor={false} content={<TTip />} />
                  <Bar dataKey="cases" fill={T.accent} radius={[0,3,3,0]} name="Cases" opacity={.85} />
                </BarChart>
              </ResponsiveContainer>
          }
        </div>

        {/* Flu heatmap — stretches to match */}
        <FluHeatmap />
      </div>

      {/* Wastewater summary bar (top 12) */}
      <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6, padding:18 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <p style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, margin:0, letterSpacing:1 }}>
            COVID-19 WASTEWATER DETECTION — CDC NWSS (TOP 12)
          </p>
          <Tag c={T.green}>CDC OPEN DATA</Tag>
        </div>
        {loading.wastewater && !ww
          ? <Spinner />
          : ww && (
            <ResponsiveContainer width="100%" height={130}>
              <BarChart data={ww.slice(0,12)} layout="vertical">
                <CartesianGrid stroke={T.border} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fill:T.textMuted, fontSize:9, fontFamily:T.mono }} tickFormatter={v=>`${v}%`} />
                <YAxis type="category" dataKey="state" tick={{ fill:T.textSecondary, fontSize:9, fontFamily:T.mono }} width={90} />
                {/* cursor={false} removes gray rectangle */}
                <Tooltip cursor={false} content={<TTip />} />
                <Bar dataKey="detectPct" name="Detect %" fill={T.green} radius={[0,3,3,0]} opacity={.85} />
              </BarChart>
            </ResponsiveContainer>
          )
        }
      </div>

      {g?.updated && (
        <p style={{ fontFamily:T.mono, fontSize:10, color:T.textMuted, margin:0, textAlign:"right" }}>
          Source: {new Date(g.updated).toUTCString()}
        </p>
      )}
    </div>
  );
}

// ── Pipelines tab ─────────────────────────────────────────────────────────────
function Pipelines({ pipeline, fetchOne }) {
  const [selected, setSelected] = useState(null);
  const okCount = Object.values(pipeline).filter(p => p.status==="success").length;

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", height:"100%", overflow:"hidden" }}>
      <div style={{ borderRight:`1px solid ${T.border}`, overflowY:"auto", padding:20 }}>
        <p style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, margin:"0 0 14px", letterSpacing:1 }}>
          DATA PIPELINE STATUS — {okCount}/{Object.keys(SOURCES).length} HEALTHY
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {Object.entries(SOURCES).map(([key, src]) => {
            const p = pipeline[key] ?? {};
            const dotS = p.status==="fetching"?"running":p.status==="success"?"healthy":p.status==="error"?"error":"idle";
            return (
              <div key={key} onClick={() => setSelected(key)} style={{
                background:selected===key?`${T.accent}0d`:T.panel,
                border:`1px solid ${selected===key?T.accent:p.status==="error"?T.red+"44":T.border}`,
                borderRadius:6, padding:16, cursor:"pointer", transition:"all .15s",
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <Dot s={dotS} />
                    <div>
                      <p style={{ fontFamily:T.sans, fontSize:13, fontWeight:600, color:T.textPrimary, margin:0 }}>{src.label}</p>
                      <p style={{ fontFamily:T.mono, fontSize:9, color:T.textMuted, margin:"2px 0 0" }}>{src.tag}</p>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }} onClick={e => e.stopPropagation()}>
                    <Tag c={p.status==="success"?T.green:p.status==="error"?T.red:p.status==="fetching"?T.accent:T.textMuted}>
                      {(p.status||"IDLE").toUpperCase()}
                    </Tag>
                    <Btn sm onClick={() => fetchOne(key)} disabled={p.status==="fetching"}>
                      {p.status==="fetching" ? <Spin /> : "▶ RUN"}
                    </Btn>
                  </div>
                </div>
                <MiniBar v={p.status==="success"?100:p.status==="fetching"?55:0}
                  c={p.status==="success"?T.green:p.status==="error"?T.red:T.accent} />
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginTop:10 }}>
                  {[["RECORDS",p.count!=null?fmtFull(p.count):"—"],
                    ["LATENCY",p.latency!=null?p.latency+"ms":"—"],
                    ["LAST RUN",p.lastRun??"Never"],
                    ["ERROR",p.error??"None"]].map(([k,v]) => (
                    <div key={k} style={{ background:T.bg, borderRadius:3, padding:"6px 8px" }}>
                      <p style={{ fontFamily:T.mono, fontSize:7, color:T.textMuted, margin:"0 0 2px", letterSpacing:1 }}>{k}</p>
                      <p style={{ fontFamily:T.mono, fontSize:10, margin:0, overflow:"hidden",
                        textOverflow:"ellipsis", whiteSpace:"nowrap",
                        color:k==="ERROR"&&p.error?T.red:T.textPrimary }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {selected ? (
          <>
            <div style={{ padding:"14px 18px", borderBottom:`1px solid ${T.border}`, background:T.surface,
              display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              <div>
                <p style={{ fontFamily:T.mono, fontSize:12, color:T.textPrimary, margin:0 }}>{SOURCES[selected].label}</p>
                <p style={{ fontFamily:T.sans, fontSize:11, color:T.textSecondary, margin:"3px 0 0" }}>
                  Python ETL · {SOURCES[selected].tag}
                </p>
              </div>
              <Tag c={T.green}>SOURCE</Tag>
            </div>
            <div style={{ flex:1, background:"#050810", overflowY:"auto", padding:18 }}>
              <pre style={{ margin:0, fontFamily:T.mono, fontSize:11.5, lineHeight:1.7, whiteSpace:"pre" }}>
                {(PIPELINE_CODE[selected]||`# No snippet for ${selected}`).split("\n").map((line,i) => {
                  let col = "#c9d8f0";
                  if (line.trim().startsWith("#")) col="#4a6685";
                  else if (/\b(from|import|def|class|return|async|await|if|for|while|with|not|in|as|yield)\b/.test(line)) col="#7eb9ff";
                  else if (/"[^"]*"|'[^']*'/.test(line)) col="#98d982";
                  else if (/^\s*@/.test(line)) col="#c9a0ff";
                  return <span key={i} style={{ color:col, display:"block" }}>{line}</span>;
                })}
              </pre>
            </div>
          </>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", gap:12 }}>
            <span style={{ fontSize:32, opacity:.2 }}>⟳</span>
            <p style={{ fontFamily:T.mono, fontSize:12, color:T.textMuted, textAlign:"center" }}>
              Select a pipeline to view<br />Python ETL source code
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── COVID-19 Tab ──────────────────────────────────────────────────────────────
function CovidTab({ data, loading, onRefresh }) {
  const [sortCol, setSortCol] = useState("cases");
  const rows = useMemo(() => [...(data??[])].sort((a,b)=>(b[sortCol]??0)-(a[sortCol]??0)), [data,sortCol]);
  const cols = [
    { key:"country",            label:"COUNTRY",    fmt:(v,r) => r.flag?<><img src={r.flag} alt="" style={{ width:18,marginRight:8,verticalAlign:"middle" }}/>{v}</>:v },
    { key:"cases",              label:"CASES",      fmt:v=>fmtFull(v) },
    { key:"deaths",             label:"DEATHS",     fmt:v=>fmtFull(v) },
    { key:"active",             label:"ACTIVE",     fmt:v=>fmtFull(v) },
    { key:"critical",           label:"CRITICAL",   fmt:v=>fmtFull(v) },
    { key:"casesPerOneMillion", label:"CASES/1M",   fmt:v=>fmtFull(v) },
    { key:"population",         label:"POPULATION", fmt:v=>fmt(v) },
  ];
  const exportRows = (data??[]).map(c=>({
    country:c.country, cases:c.cases, deaths:c.deaths, recovered:c.recovered,
    active:c.active, critical:c.critical, "cases/1M":c.casesPerOneMillion, population:c.population,
  }));
  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ padding:"10px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", gap:8,
        background:T.surface, alignItems:"center" }}>
        <Btn sm onClick={onRefresh}>↻ REFRESH</Btn>
        <Btn sm c={T.green}  onClick={()=>exportCSV(exportRows,"covid19-countries")}>↓ CSV</Btn>
        <Btn sm c={T.purple} onClick={()=>exportJSON(exportRows,"covid19-countries")}>↓ JSON</Btn>
        <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, marginLeft:8 }}>
          disease.sh · click column to sort · {data?.length??0} countries
        </span>
        {loading.countries && <Spin />}
      </div>
      {loading.countries&&!data?<Spinner />:(
        <div style={{ overflowY:"auto", flex:1 }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead style={{ position:"sticky", top:0, background:T.surface, zIndex:1 }}>
              <tr>
                {cols.map(c=>(
                  <th key={c.key} onClick={()=>setSortCol(c.key)} style={{
                    padding:"10px 14px", textAlign:"left", fontFamily:T.mono, fontSize:9,
                    color:sortCol===c.key?T.accent:T.textSecondary, letterSpacing:1.2,
                    cursor:"pointer", borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap",
                  }}>{c.label} {sortCol===c.key?"↓":""}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}15` }}
                  onMouseEnter={e=>e.currentTarget.style.background=`${T.accent}08`}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {cols.map(c=>(
                    <td key={c.key} style={{
                      padding:"8px 14px", fontFamily:c.key==="country"?T.sans:T.mono,
                      fontSize:c.key==="country"?13:12,
                      color:c.key==="cases"?T.red:c.key==="deaths"?T.amber:c.key==="critical"?T.purple:T.textPrimary,
                      fontWeight:c.key==="country"?600:400,
                    }}>{c.fmt(row[c.key],row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── FDA Events Tab ────────────────────────────────────────────────────────────
function FDATab({ raw, loading, onRefresh }) {
  const data = useMemo(()=>raw?transformFDA(raw):null,[raw]);
  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ padding:"10px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", gap:8, background:T.surface, alignItems:"center" }}>
        <Btn sm onClick={onRefresh}>↻ REFRESH</Btn>
        <Btn sm c={T.green}  onClick={()=>exportCSV(data,"fda-adverse-events")}>↓ CSV</Btn>
        <Btn sm c={T.purple} onClick={()=>exportJSON(data,"fda-adverse-events")}>↓ JSON</Btn>
        <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, marginLeft:8 }}>
          api.fda.gov · adverse drug event counts by receive date
        </span>
        {loading.fda&&<Spin/>}
      </div>
      {loading.fda&&!data?<Spinner/>:!data?null:(
        <div style={{ display:"flex", flexDirection:"column", gap:14, padding:20, overflowY:"auto" }}>
          <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:`1px solid ${T.border}`, background:T.surface }}>
              <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, letterSpacing:1 }}>ADVERSE EVENTS TIMELINE — 24 MONTHS</span>
            </div>
            <div style={{ padding:"16px 16px 8px" }}>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data}>
                  <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill:T.textMuted, fontSize:9, fontFamily:T.mono }} />
                  <YAxis tick={{ fill:T.textMuted, fontSize:9, fontFamily:T.mono }} />
                  <Tooltip content={<TTip/>}/>
                  <Line type="monotone" dataKey="events" stroke={T.purple} strokeWidth={2} dot={{ r:3, fill:T.purple }} name="Events" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:6, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:`1px solid ${T.border}`, background:T.surface }}>
              <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary }}>RAW RECORDS</span>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ borderBottom:`1px solid ${T.border}` }}>
                {["DATE","FULL DATE","EVENT COUNT"].map(h=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontFamily:T.mono, fontSize:9, color:T.textSecondary, letterSpacing:1.2 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.map((row,i)=>(
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}15` }}>
                    <td style={{ padding:"7px 14px", fontFamily:T.mono, fontSize:12, color:T.textPrimary }}>{row.date}</td>
                    <td style={{ padding:"7px 14px", fontFamily:T.mono, fontSize:12, color:T.textSecondary }}>{row.fullDate}</td>
                    <td style={{ padding:"7px 14px", fontFamily:T.mono, fontSize:12, color:T.purple }}>{(row.events??0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Wastewater Tab — split layout: scrollable chart + scrollable table ─────────
function WastewaterTab({ raw, loading, onRefresh }) {
  const data = useMemo(()=>raw?transformWastewater(raw):null,[raw]);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ padding:"10px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", gap:8,
        background:T.surface, alignItems:"center" }}>
        <Btn sm onClick={onRefresh}>↻ REFRESH</Btn>
        <Btn sm c={T.green}  onClick={()=>exportCSV(data,"cdc-nwss-wastewater")}>↓ CSV</Btn>
        <Btn sm c={T.purple} onClick={()=>exportJSON(data,"cdc-nwss-wastewater")}>↓ JSON</Btn>
        <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, marginLeft:8 }}>
          data.cdc.gov · CDC NWSS · {data?.length??0} jurisdictions
        </span>
        {loading.wastewater&&<Spin/>}
      </div>

      {loading.wastewater&&!data?<Spinner/>:!data?null:(
        <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", overflow:"hidden" }}>

          {/* LEFT — scrollable bar chart that grows with data */}
          <div style={{ display:"flex", flexDirection:"column", borderRight:`1px solid ${T.border}`, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:`1px solid ${T.border}`, background:T.surface, flexShrink:0 }}>
              <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, letterSpacing:1 }}>
                DETECTION RATE BY JURISDICTION
              </span>
            </div>
            {/* The chart container scrolls; chart height grows with # of bars */}
            <div style={{ flex:1, overflowY:"auto", overflowX:"hidden" }}>
              <div style={{ height: Math.max(320, data.length * 28), padding:"8px 8px 8px 0" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} layout="vertical" margin={{ left:8, right:16, top:4, bottom:4 }}>
                    <CartesianGrid stroke={T.border} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fill:T.textMuted, fontSize:9, fontFamily:T.mono }}
                      tickFormatter={v=>`${v}%`} />
                    <YAxis type="category" dataKey="state"
                      tick={{ fill:T.textSecondary, fontSize:9, fontFamily:T.mono }} width={110} />
                    {/* cursor={false} — removes the gray hover rectangle on bar charts */}
                    <Tooltip cursor={false} content={<TTip/>}/>
                    <Bar dataKey="detectPct" name="Detect %" fill={T.green} radius={[0,3,3,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* RIGHT — scrollable table showing ALL jurisdictions */}
          <div style={{ display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", borderBottom:`1px solid ${T.border}`, background:T.surface, flexShrink:0 }}>
              <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary, letterSpacing:1 }}>
                ALL JURISDICTIONS — {data.length} TOTAL
              </span>
            </div>
            {/* This div scrolls independently */}
            <div style={{ flex:1, overflowY:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead style={{ position:"sticky", top:0, background:T.surface, zIndex:1 }}>
                  <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                    {["#","JURISDICTION","SITES","DETECT %"].map(h=>(
                      <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontFamily:T.mono,
                        fontSize:9, color:T.textSecondary, letterSpacing:1.2, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row,i)=>(
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}15` }}
                      onMouseEnter={e=>e.currentTarget.style.background=`${T.accent}08`}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"8px 14px", fontFamily:T.mono, fontSize:11, color:T.textMuted }}>{i+1}</td>
                      <td style={{ padding:"8px 14px", fontFamily:T.sans, fontSize:13, color:T.textPrimary, fontWeight:600 }}>{row.state}</td>
                      <td style={{ padding:"8px 14px", fontFamily:T.mono, fontSize:12, color:T.textSecondary }}>{row.sites}</td>
                      <td style={{ padding:"8px 14px", fontFamily:T.mono, fontSize:13, fontWeight:600,
                        color:row.detectPct>50?T.red:row.detectPct>25?T.amber:T.green }}>{row.detectPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Datasets Tab ──────────────────────────────────────────────────────────────
function DatasetsTab({ data, imported, onImport, onRemove }) {
  const [activeDs, setActiveDs] = useState("countries");
  const [importErr, setImportErr] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  function handleFiles(files) {
    setImportErr("");
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          let rows = file.name.endsWith(".json") ? JSON.parse(e.target.result) : parseCSVText(e.target.result);
          if (!Array.isArray(rows)) rows=[rows];
          if (!rows.length) throw new Error("No rows found");
          onImport({ name:file.name.replace(/\.[^.]+$/,""), rows });
        } catch(err) { setImportErr(err.message); }
      };
      reader.readAsText(file);
    });
  }

  const builtIn = {
    countries:{
      label:"COVID by Country", tag:"disease.sh · LIVE", tc:T.accent,
      rows:(data.countries??[]).slice(0,20).map(c=>({
        Country:c.country, Cases:fmtFull(c.cases), Deaths:fmtFull(c.deaths),
        Recovered:fmtFull(c.recovered), Active:fmtFull(c.active),
        "Cases/1M":fmtFull(c.casesPerOneMillion), Population:fmtFull(c.population),
      })),
    },
    fda:{ label:"FDA Adverse Events", tag:"OpenFDA · LIVE", tc:T.purple, rows:data.fda?transformFDA(data.fda):[] },
    wastewater:{ label:"CDC Wastewater", tag:"CDC NWSS · LIVE", tc:T.green, rows:data.wastewater?transformWastewater(data.wastewater):[] },
  };
  const importedDs = Object.fromEntries(imported.map(d=>[d.name,{ label:d.name+" (Imported)", tag:"IMPORTED", tc:T.amber, rows:d.rows, imp:true }]));
  const allDs = { ...builtIn, ...importedDs };
  const ds    = allDs[activeDs]??Object.values(allDs)[0];
  const cols  = ds?.rows?.[0] ? Object.keys(ds.rows[0]) : [];

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", gap:5, padding:"10px 18px",
        borderBottom:`1px solid ${T.border}`, background:T.surface, flexWrap:"wrap" }}>
        {Object.entries(allDs).map(([k,d])=>(
          <button key={k} onClick={()=>setActiveDs(k)} style={{
            background:activeDs===k?`${T.accent}18`:"none",
            border:`1px solid ${activeDs===k?T.accent:T.border}`,
            color:activeDs===k?T.accent:T.textSecondary,
            fontFamily:T.mono, fontSize:9, letterSpacing:.8,
            padding:"5px 10px", borderRadius:4, cursor:"pointer", transition:"all .15s", whiteSpace:"nowrap",
          }}>{d.label}</button>
        ))}
        <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
          {importErr&&<span style={{ fontFamily:T.mono, fontSize:10, color:T.red }}>⚠ {importErr}</span>}
          <div
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{e.preventDefault();setDragOver(false);handleFiles(e.dataTransfer.files);}}>
            <Btn sm c={dragOver?T.accent:T.amber} onClick={()=>fileRef.current?.click()}>
              {dragOver?"DROP HERE":"⬆ IMPORT"}
            </Btn>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.json" multiple onChange={e=>handleFiles(e.target.files)} style={{ display:"none" }} />
          {ds?.rows?.length>0&&<>
            <Btn sm c={T.green}  onClick={()=>exportCSV(ds.rows,activeDs)}>⬇ CSV</Btn>
            <Btn sm c={T.purple} onClick={()=>exportJSON(ds.rows,activeDs)}>⬇ JSON</Btn>
          </>}
          {ds?.imp&&<Btn sm c={T.red} onClick={()=>{onRemove(activeDs);setActiveDs("countries");}}>✕ REMOVE</Btn>}
        </div>
      </div>
      <div style={{ flex:1, overflow:"auto" }}>
        {!ds?.rows?.length
          ? <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <p style={{ fontFamily:T.mono, fontSize:12, color:T.textMuted }}>
                No data — drag &amp; drop .csv/.json to import, or fetch live data first
              </p>
            </div>
          : <>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 20px 8px" }}>
                <span style={{ fontFamily:T.mono, fontSize:10, color:T.textSecondary }}>
                  {fmtFull(ds.rows.length)} ROWS · {cols.length} COLS
                </span>
                <Tag c={ds.tc}>{ds.tag}</Tag>
              </div>
              <div style={{ overflowX:"auto", padding:"0 20px 20px" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:T.mono, fontSize:11 }}>
                  <thead>
                    <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                      {cols.map(c=>(
                        <th key={c} style={{ padding:"8px 12px", textAlign:"left", color:T.textSecondary,
                          fontSize:9, letterSpacing:1, fontWeight:400, whiteSpace:"nowrap",
                          background:T.panel, position:"sticky", top:0 }}>{c.toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ds.rows.map((row,i)=>(
                      <tr key={i} style={{ borderBottom:`1px solid ${T.border}15`, background:i%2===0?T.panel:T.bg }}
                        onMouseEnter={e=>e.currentTarget.style.background=`${T.accent}0a`}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?T.panel:T.bg}>
                        {cols.map(c=>(
                          <td key={c} style={{ padding:"7px 12px", color:T.textPrimary,
                            whiteSpace:"nowrap", maxWidth:220, overflow:"hidden", textOverflow:"ellipsis" }}>
                            {String(row[c]??"—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
        }
      </div>
    </div>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────
function LogsTab({ logs, clearLogs }) {
  const [filter, setFilter]       = useState("ALL");
  const [search, setSearch]       = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const endRef = useRef();
  const lc = { INFO:T.green, WARN:T.amber, ERROR:T.red, DEBUG:T.purple, FETCH:T.accent };

  useEffect(()=>{
    if (autoScroll&&endRef.current) endRef.current.scrollIntoView({ behavior:"smooth" });
  },[logs,autoScroll]);

  const visible = useMemo(()=>logs.filter(l=>
    (filter==="ALL"||l.level===filter)&&
    (!search||l.msg.toLowerCase().includes(search.toLowerCase())||l.svc.toLowerCase().includes(search.toLowerCase()))
  ),[logs,filter,search]);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 20px",
        borderBottom:`1px solid ${T.border}`, background:T.surface, flexShrink:0, flexWrap:"wrap" }}>
        <div style={{ display:"flex", gap:3 }}>
          {["ALL","FETCH","INFO","WARN","ERROR","DEBUG"].map(lvl=>(
            <button key={lvl} onClick={()=>setFilter(lvl)} style={{
              background:filter===lvl?`${lc[lvl]??T.accent}22`:"none",
              border:`1px solid ${filter===lvl?(lc[lvl]??T.accent):T.border}`,
              color:filter===lvl?(lc[lvl]??T.accent):T.textSecondary,
              fontFamily:T.mono, fontSize:9, letterSpacing:.8, padding:"4px 9px", borderRadius:3, cursor:"pointer",
            }}>{lvl}</button>
          ))}
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search logs…"
          style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:4,
            padding:"4px 10px", fontFamily:T.mono, fontSize:11, color:T.textPrimary,
            outline:"none", width:160 }} />
        <span style={{ fontFamily:T.mono, fontSize:10, color:T.textMuted }}>
          {fmtFull(visible.length)}/{fmtFull(logs.length)}
        </span>
        <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center" }}>
          <label style={{ display:"flex", alignItems:"center", gap:5, fontFamily:T.mono,
            fontSize:9, color:T.textSecondary, cursor:"pointer" }}>
            <input type="checkbox" checked={autoScroll} onChange={e=>setAutoScroll(e.target.checked)} />
            Auto-scroll
          </label>
          <Btn sm c={T.green}  onClick={()=>exportLogs(visible,"csv")}>⬇ CSV</Btn>
          <Btn sm c={T.accent} onClick={()=>exportLogs(visible,"txt")}>⬇ TXT</Btn>
          <Btn sm c={T.purple} onClick={()=>exportJSON(visible,"pulse-logs")}>⬇ JSON</Btn>
          <Btn sm c={T.red}    onClick={clearLogs}>✕ CLEAR</Btn>
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"8px 16px", background:"#05080f",
        display:"flex", flexDirection:"column", gap:1 }}>
        {visible.length===0
          ? <p style={{ fontFamily:T.mono, fontSize:12, color:T.textMuted, textAlign:"center", paddingTop:40 }}>No entries</p>
          : visible.map((l,i)=>(
            <div key={l.id??i} style={{
              display:"grid", gridTemplateColumns:"72px 50px 130px 1fr", gap:10,
              fontFamily:T.mono, fontSize:11.5, lineHeight:1.7, padding:"1px 6px", borderRadius:3,
              background:l.level==="ERROR"?`${T.red}08`:l.level==="WARN"?`${T.amber}06`:"transparent",
            }}>
              <span style={{ color:T.textMuted }}>{l.t}</span>
              <span style={{ color:lc[l.level]??T.textMuted, fontWeight:600 }}>{l.level}</span>
              <span style={{ color:T.accentDim, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>[{l.svc}]</span>
              <span style={{ color:l.level==="ERROR"?T.red:l.level==="WARN"?T.amber:T.textPrimary }}>{l.msg}</span>
            </div>
          ))
        }
        <div ref={endRef}/>
      </div>
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [tab,       setTab]       = useState("dashboard");
  const [clock,     setClock]     = useState(nowTs());
  const [countdown, setCountdown] = useState(60);
  const [busy,      setBusy]      = useState(false);
  const [logs,      setLogs]      = useState([]);
  const [imported,  setImported]  = useState([]);
  const [data,      setData]      = useState({
    global:null, historical:null, countries:null, continents:null, fda:null, wastewater:null,
  });
  const [loading, setLoading] = useState({
    global:false, historical:false, countries:false, continents:false, fda:false, wastewater:false,
  });
  const [pipeline, setPipeline] = useState({});

  const addLog = useCallback((level, msg, svc="system") => {
    setLogs(l=>[...l,{ t:nowTs(), level, msg, svc, id:Date.now()+Math.random() }].slice(-500));
  },[]);

  const fetchOne = useCallback(async (key) => {
    const src=SOURCES[key], t0=Date.now();
    addLog("FETCH",`→ GET ${src.url}`,key);
    setPipeline(p=>({...p,[key]:{...p[key],status:"fetching",error:null}}));
    setLoading(l=>({...l,[key]:true}));
    try {
      const res=await fetch(src.url,{headers:{Accept:"application/json"}});
      const latency=Date.now()-t0;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json=await res.json();
      const count=Array.isArray(json)?json.length:1;
      setData(d=>({...d,[key]:json}));
      setPipeline(p=>({...p,[key]:{status:"success",count,latency,lastRun:nowTs(),statusCode:res.status,error:null}}));
      addLog("INFO",`✓ ${src.label}: ${fmtFull(count)} record(s) in ${latency}ms`,key);
    } catch(err) {
      const latency=Date.now()-t0;
      setPipeline(p=>({...p,[key]:{status:"error",latency,lastRun:nowTs(),statusCode:"ERR",error:err.message,count:null}}));
      addLog("ERROR",`✗ ${src.label}: ${err.message}`,key);
    } finally {
      setLoading(l=>({...l,[key]:false}));
    }
  },[addLog]);

  const fetchAll = useCallback(async()=>{
    setBusy(true);
    addLog("INFO","Full refresh started","orchestrator");
    await Promise.all(Object.keys(SOURCES).map(fetchOne));
    setBusy(false);
    addLog("INFO","Full refresh complete","orchestrator");
    setCountdown(60);
  },[fetchOne,addLog]);

  useEffect(()=>{ fetchAll(); },[]);
  useEffect(()=>{
    const cid=setInterval(()=>setClock(nowTs()),1000);
    const rid=setInterval(()=>setCountdown(c=>{ if(c<=1){fetchAll();return 60;} return c-1; }),1000);
    return()=>{ clearInterval(cid); clearInterval(rid); };
  },[fetchAll]);

  const ok=Object.values(pipeline).filter(p=>p.status==="success").length;
  const tot=Object.keys(SOURCES).length;
  const hasErr=Object.values(pipeline).some(p=>p.status==="error");

  const pipeStatus = {
    covid:      pipeline.countries?.status==="success"?"healthy":pipeline.countries?.status==="error"?"error":"idle",
    fda:        pipeline.fda?.status==="success"?"healthy":pipeline.fda?.status==="error"?"error":"idle",
    wastewater: pipeline.wastewater?.status==="success"?"healthy":pipeline.wastewater?.status==="error"?"error":"idle",
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh",
      background:T.bg, color:T.textPrimary, fontFamily:T.sans }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@400;600&display=swap');
        * { box-sizing:border-box; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:${T.bg}; }
        ::-webkit-scrollbar-thumb { background:${T.border}; border-radius:2px; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes spin  { to{transform:rotate(360deg)} }
        button:hover:not(:disabled) { filter:brightness(1.2); }
        input::placeholder { color:${T.textMuted}; }
        select option { background:${T.panel}; }
        th { user-select:none; }
      `}</style>

      <header style={{ background:T.surface, borderBottom:`1px solid ${T.border}`,
        padding:"10px 24px", display:"flex", justifyContent:"space-between",
        alignItems:"center", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:32, height:32, background:`linear-gradient(135deg,${T.accent},${T.purple})`,
            borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700 }}>⬡</div>
          <div>
            <span style={{ fontFamily:T.mono, fontSize:15, fontWeight:700, color:T.textPrimary, letterSpacing:2 }}>PULSE</span>
            <span style={{ fontFamily:T.mono, fontSize:10, color:T.textMuted, marginLeft:12 }}>Public Health Analytics Platform</span>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <Tag c={hasErr?T.amber:T.green}>{hasErr?"⚠ DEGRADED":"● LIVE"} {ok}/{tot}</Tag>
          <span style={{ fontFamily:T.mono, fontSize:11, color:T.textMuted }}>{clock} UTC</span>
          <div style={{ width:30, height:30, borderRadius:"50%", background:`${T.accent}22`,
            border:`1px solid ${T.accentDim}`, display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:11, color:T.accent, fontWeight:700, fontFamily:T.mono }}>SJ</div>
        </div>
      </header>

      <Nav tab={tab} setTab={setTab} countdown={countdown} onRefresh={fetchAll} busy={busy} pipeStatus={pipeStatus} />

      <main style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
        {tab==="dashboard"  && <div style={{ flex:1, overflowY:"auto" }}><Dashboard data={data} loading={loading} /></div>}
        {tab==="pipelines"  && <Pipelines pipeline={pipeline} fetchOne={fetchOne} />}
        {tab==="covid"      && <CovidTab data={data.countries} loading={loading} onRefresh={fetchAll} />}
        {tab==="fda"        && <FDATab raw={data.fda} loading={loading} onRefresh={()=>fetchOne("fda")} />}
        {tab==="wastewater" && <WastewaterTab raw={data.wastewater} loading={loading} onRefresh={()=>fetchOne("wastewater")} />}
        {tab==="datasets"   && (
          <DatasetsTab
            data={data}
            imported={imported}
            onImport={ds=>{
              setImported(prev=>[...prev.filter(x=>x.name!==ds.name),ds]);
              addLog("INFO",`Imported "${ds.name}" (${fmtFull(ds.rows.length)} rows)`,"import");
            }}
            onRemove={name=>{
              setImported(prev=>prev.filter(x=>x.name!==name));
              addLog("INFO",`Removed import "${name}"`,"import");
            }}
          />
        )}
        {tab==="logs" && <LogsTab logs={logs} clearLogs={()=>setLogs([])} />}
      </main>
    </div>
  );
}